// Barcode localisation — our own, classical computer vision; no ML, no DOM. Finds
// barcode-shaped regions in a grayscale image and returns rotated rectangles with
// orientation. ZXing then decodes each crop (rotated to horizontal), which is faster
// than scanning whole frames and works at any angle; the rectangles give steady,
// honest outlines (chapter 7 §7.2).
//
// Pipeline (on a downscaled image; bars must stay ≥ ~1.5 px wide, so ~640 px for a
// 720p camera frame):
//   gradient → signed edges + structure tensor → box blur → alternating edge density
//   × orientation coherence (1D) or incoherence (2D) → threshold → connected
//   components → PCA orientation and extents → rotated rectangle + score.

export interface Gray {
  data: Uint8ClampedArray | Uint8Array; // one byte per pixel
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Region {
  kind: "1d" | "2d";
  /** Corners in the coordinate system of the image given to locate() (clockwise). */
  corners: [Point, Point, Point, Point];
  center: Point;
  /** Orientation of the long axis in radians (0 = horizontal). For 1D codes the bars run across this axis. */
  angle: number;
  /** Half-lengths along the long and short axes. */
  halfLong: number;
  halfShort: number;
  area: number;
  score: number;
}

/** RGBA → gray, downscaled by an integer factor with box averaging. */
export function toGray(rgba: Uint8ClampedArray | Uint8Array, width: number, height: number, factor: number): Gray {
  if (factor === 1) {
    const out = new Uint8ClampedArray(width * height);
    for (let i = 0, o = 0; o < out.length; i += 4, o++) out[o] = (rgba[i]! * 77 + rgba[i + 1]! * 150 + rgba[i + 2]! * 29) >> 8;
    return { data: out, width, height };
  }
  const w = Math.floor(width / factor);
  const h = Math.floor(height / factor);
  const out = new Uint8ClampedArray(w * h);
  const n = factor * factor;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      for (let dy = 0; dy < factor; dy++) {
        let i = ((y * factor + dy) * width + x * factor) * 4;
        for (let dx = 0; dx < factor; dx++, i += 4) {
          // luma, integer weights
          sum += (rgba[i]! * 77 + rgba[i + 1]! * 150 + rgba[i + 2]! * 29) >> 8;
        }
      }
      out[y * w + x] = sum / n;
    }
  }
  return { data: out, width: w, height: h };
}

/** Box blur of a float map using an integral image (radius r). */
function boxBlur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const integral = new Float64Array((w + 1) * (h + 1));
  for (let y = 1; y <= h; y++) {
    let row = 0;
    for (let x = 1; x <= w; x++) {
      row += src[(y - 1) * w + (x - 1)]!;
      integral[y * (w + 1) + x] = integral[(y - 1) * (w + 1) + x]! + row;
    }
  }
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const y0 = Math.max(0, y - r);
    const y1 = Math.min(h, y + r + 1);
    for (let x = 0; x < w; x++) {
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w, x + r + 1);
      const s = integral[y1 * (w + 1) + x1]! - integral[y0 * (w + 1) + x1]! - integral[y1 * (w + 1) + x0]! + integral[y0 * (w + 1) + x0]!;
      out[y * w + x] = s / ((y1 - y0) * (x1 - x0));
    }
  }
  return out;
}

export interface LocateOptions {
  /** Blur radius in downscaled pixels; ~ half a bar period or more. */
  blurRadius?: number;
  /** Minimum gradient (0–255) for a pixel to count as an edge. */
  edgeMin?: number;
  /** Minimum local edge density (one direction minus the other) for 1D regions. */
  density1d?: number;
  /** Minimum local edge density (both directions) for 2D regions. */
  density2d?: number;
  /** Minimum orientation coherence (0–1) for a 1D region. */
  coherenceMin?: number;
  /** Maximum orientation coherence for a 2D region. */
  coherenceMax2d?: number;
  /** Minimum component area in downscaled pixels. */
  minArea?: number;
  /** Minimum long/short ratio to call a region a 1D code. */
  minAspect1d?: number;
  /** Maximum long/short ratio for a 1D code; longer and thinner is a line. */
  maxAspect1d?: number;
  maxRegions?: number;
  /** Downscale factor from the frame to the maps edges are accumulated into. */
  bin?: number;
  /** Gradient baseline in pixels: 2 (central difference) or 1 (adjacent pixels, more
   *  sensitive to modules under ~2 px). */
  gradStep?: 1 | 2;
  /** Pixel budget for the localisation pass (frames above it are reduced first). */
  budgetPx?: number;
  /** Receives the internal maps (for debugging/benchmarks only). */
  debug?: (maps: { e1: Float32Array; e3: Float32Array; width: number; height: number }) => void;
}

/**
 * Locate barcode-like regions. `gray` is the full-resolution frame; edges are measured
 * on it (fine modules must keep their pixels) and binned `bin`× down for everything
 * else. Returned geometry is in full-frame coordinates.
 */
export function locate(gray: Gray, opts: LocateOptions = {}): Region[] {
  const bin = opts.bin ?? 2;
  const { width: W, height: H, data } = gray;
  const w = Math.floor(W / bin);
  const h = Math.floor(H / bin);
  const blurRadius = opts.blurRadius ?? 5;
  const density1d = opts.density1d ?? 0.3;
  const density2d = opts.density2d ?? 0.12;
  const coherenceMin = opts.coherenceMin ?? 0.6;
  const coherenceMax2d = opts.coherenceMax2d ?? 0.4;
  const minArea = opts.minArea ?? 40;
  const minAspect1d = opts.minAspect1d ?? 1.6;
  const maxAspect1d = opts.maxAspect1d ?? 30;
  const maxRegions = opts.maxRegions ?? 12;

  // 1. Per pixel (full resolution): is it an edge, with which sign, and the structure
  //    tensor terms; accumulated into bins. A barcode is a dense field of edges that
  //    alternate in sign (dark→light→dark) and all share one orientation (high
  //    coherence). Text is dense but incoherent; a border or table rule is coherent but
  //    sparse and hair-thin (filtered on shape below). Nothing assumes an axis.
  const n = w * h;
  const ep = new Float32Array(n); // positive-sign edges
  const en = new Float32Array(n); // negative-sign edges
  const jxx = new Float32Array(n);
  const jyy = new Float32Array(n);
  const jxy = new Float32Array(n);
  const edgeMin = opts.edgeMin ?? 24;
  const inv = 1 / (bin * bin);
  const g1 = (opts.gradStep ?? 2) === 1;
  for (let y = 1; y < h * bin - 1; y++) {
    const row = y * W;
    const brow = Math.floor(y / bin) * w;
    for (let x = 1; x < w * bin - 1; x++) {
      const i = row + x;
      const gx = g1 ? (data[i + 1]! - data[i]!) * 2 : data[i + 1]! - data[i - 1]!;
      const gy = g1 ? (data[i + W]! - data[i]!) * 2 : data[i + W]! - data[i - W]!;
      const ax = gx < 0 ? -gx : gx;
      const ay = gy < 0 ? -gy : gy;
      if (ax < edgeMin && ay < edgeMin) continue;
      const b = brow + ((x / bin) | 0);
      // Sign along the dominant axis; consistent for one edge, opposite for the next bar.
      const sign = ax >= ay ? gx : gy;
      if (sign > 0) ep[b]! += inv; else en[b]! += inv;
      // Normalised tensor (orientation only, not magnitude).
      const m = 1 / Math.sqrt(gx * gx + gy * gy);
      const nx = gx * m;
      const ny = gy * m;
      jxx[b]! += nx * nx;
      jyy[b]! += ny * ny;
      jxy[b]! += nx * ny;
    }
  }
  const dp = boxBlur(ep, w, h, blurRadius);
  const dn = boxBlur(en, w, h, blurRadius);
  const bxx = boxBlur(jxx, w, h, blurRadius);
  const byy = boxBlur(jyy, w, h, blurRadius);
  const bxy = boxBlur(jxy, w, h, blurRadius);
  const e1 = new Float32Array(n); // 1D: alternating density × coherence
  const e3 = new Float32Array(n); // 2D: alternating density × incoherence
  for (let i = 0; i < n; i++) {
    const density = 2 * Math.min(dp[i]!, dn[i]!);
    if (density === 0) continue;
    const tr = bxx[i]! + byy[i]!;
    const d = bxx[i]! - byy[i]!;
    const coherence = tr > 0 ? Math.sqrt(d * d + 4 * bxy[i]! * bxy[i]!) / tr : 0;
    e1[i] = coherence >= coherenceMin ? density : 0;
    e3[i] = coherence < coherenceMax2d ? density : 0;
  }

  opts.debug?.({ e1, e3, width: w, height: h });
  const pixels: number[] = [];
  const regions: Region[] = [];
  const maps: Array<{ map: Float32Array; kind: "1d" | "2d" }> = [
    { map: e1, kind: "1d" },
    { map: e3, kind: "2d" },
  ];

  for (const { map, kind } of maps) {
    // 2. Threshold on density (fraction of pixels that are edges of the given direction).
    const threshold = kind === "2d" ? density2d : density1d;
    const bin = new Uint8Array(w * h);
    for (let i = 0; i < bin.length; i++) bin[i] = map[i]! >= threshold ? 1 : 0;

    // 3. Connected components.
    const labels = new Int32Array(w * h);
    const stack: number[] = [];
    let label = 0;
    for (let start = 0; start < bin.length; start++) {
      if (bin[start] !== 1 || labels[start] !== 0) continue;
      label++;
      pixels.length = 0;
      let se = 0;
      stack.push(start);
      labels[start] = label;
      while (stack.length) {
        const i = stack.pop()!;
        pixels.push(i);
        se += map[i]!;
        const x = i % w;
        if (x > 0 && bin[i - 1] === 1 && labels[i - 1] === 0) { labels[i - 1] = label; stack.push(i - 1); }
        if (x < w - 1 && bin[i + 1] === 1 && labels[i + 1] === 0) { labels[i + 1] = label; stack.push(i + 1); }
        if (i >= w && bin[i - w] === 1 && labels[i - w] === 0) { labels[i - w] = label; stack.push(i - w); }
        if (i + w < bin.length && bin[i + w] === 1 && labels[i + w] === 0) { labels[i + w] = label; stack.push(i + w); }
      }
      const n = pixels.length;
      if (n < minArea) continue;
      // 4. Orientation from the shape of the blob's dense core (density well above the
      //    threshold): the core is the bars; weaker fringes are neighbouring text the
      //    blob may have swallowed. (Gradient direction is not used: on fine modules it
      //    follows sampling moiré rather than the bars.) Extents from the whole blob.
      let sx = 0, sy = 0, sxx = 0, syy = 0, sxy = 0, m = 0;
      const coreMin = threshold * 1.5;
      for (let pass = 0; pass < 2 && m < minArea; pass++) {
        sx = sy = sxx = syy = sxy = m = 0;
        const lim = pass === 0 ? coreMin : threshold;
        for (const i of pixels) {
          if (map[i]! < lim) continue;
          const x = i % w, y = (i - x) / w;
          sx += x; sy += y; sxx += x * x; syy += y * y; sxy += x * y; m++;
        }
      }
      const cxm = sx / m, cym = sy / m;
      let angle = 0.5 * Math.atan2(2 * (sxy / m - cxm * cym), sxx / m - cxm * cxm - (syy / m - cym * cym));
      const ux = Math.cos(angle), uy = Math.sin(angle);
      let aMin = Infinity, aMax = -Infinity, bMin = Infinity, bMax = -Infinity;
      for (const i of pixels) {
        const x = i % w, y = (i - x) / w;
        const a = x * ux + y * uy;
        const b = -x * uy + y * ux;
        if (a < aMin) aMin = a; if (a > aMax) aMax = a;
        if (b < bMin) bMin = b; if (b > bMax) bMax = b;
      }
      // The blur spreads density by ~blurRadius beyond the bars on every side.
      let halfLong = (aMax - aMin) / 2 - blurRadius;
      let halfShort = (bMax - bMin) / 2 - blurRadius;
      // Aspect from the blob's extents before the blur margin is removed: a flat code
      // (M Food GS1-128, 14:1 at 100 px tall in a 4K frame) would otherwise look 22:1.
      let aspect = (aMax - aMin) / Math.max(1, bMax - bMin);
      if (halfLong < halfShort) { const t = halfLong; halfLong = halfShort; halfShort = t; angle += Math.PI / 2; aspect = 1 / aspect; }
      halfLong = Math.max(2, halfLong);
      halfShort = Math.max(2, halfShort);
      // Shape: barcodes are a few bar heights wide; rules and borders are hair-thin and
      // far longer than any code (GS1 keeps bar height ≥ ~15 % of width).
      if (kind === "1d" && (aspect < minAspect1d || aspect > maxAspect1d)) continue;
      if (kind === "2d" && aspect > 1.8) continue;
      if (halfShort < 2) continue;
      const ca = (aMin + aMax) / 2, cb = (bMin + bMax) / 2;
      const u2x = Math.cos(angle), u2y = Math.sin(angle);
      const vx = -u2y, vy = u2x;
      // centre: back from (a, b) along the *original* axes
      const cx = ca * ux + cb * -uy;
      const cy = ca * uy + cb * ux;
      const corner = (a: number, b: number): Point => ({ x: cx + u2x * a + vx * b, y: cy + u2y * a + vy * b });
      regions.push({
        kind,
        corners: [corner(-halfLong, -halfShort), corner(halfLong, -halfShort), corner(halfLong, halfShort), corner(-halfLong, halfShort)],
        center: { x: cx, y: cy },
        angle,
        halfLong,
        halfShort,
        area: n,
        score: (se / n) * Math.sqrt(n),
      });
    }
  }

  // 5. Suppress duplicates (the same code found in two maps or split in two): keep the
  //    stronger of any pair whose centres fall inside the other's rectangle.
  regions.sort((a, b) => b.score - a.score);
  const kept: Region[] = [];
  for (const r of regions) {
    if (kept.some((k) => inside(r.center, k) || inside(k.center, r))) continue;
    kept.push(r);
    if (kept.length >= maxRegions) break;
  }
  return bin === 1 ? kept : kept.map((r) => scaleRegion(r, bin));
}

function inside(p: Point, r: Region): boolean {
  const dx = p.x - r.center.x;
  const dy = p.y - r.center.y;
  const u = dx * Math.cos(r.angle) + dy * Math.sin(r.angle);
  const v = -dx * Math.sin(r.angle) + dy * Math.cos(r.angle);
  return Math.abs(u) <= r.halfLong * 1.1 && Math.abs(v) <= r.halfShort * 1.1;
}

/** Scale a region's geometry from the downscaled image back to the full frame. */
export function scaleRegion(r: Region, factor: number): Region {
  const s = (p: Point): Point => ({ x: p.x * factor, y: p.y * factor });
  return {
    ...r,
    corners: [s(r.corners[0]), s(r.corners[1]), s(r.corners[2]), s(r.corners[3])],
    center: s(r.center),
    halfLong: r.halfLong * factor,
    halfShort: r.halfShort * factor,
    area: r.area * factor * factor,
  };
}

export interface Crop {
  data: Uint8ClampedArray;
  width: number;
  height: number;
  resampled: boolean;
  /** Map a point in crop pixels back to frame pixels. */
  toFrame: (p: Point) => Point;
}

/**
 * Extract a region from a full-resolution RGBA image as a gray-in-RGBA crop with the
 * code's long axis horizontal, with padding.
 *
 * Small tilts take a raw axis-aligned crop: no resampling, so fine modules keep their
 * native pixels, and the decoder copes with a few degrees as it does on a whole frame.
 * Larger angles rotate with bilinear sampling at `scale`× density (nearest-neighbour
 * jitters module widths by a pixel, which breaks decoding of fine bars).
 */
/** Half-extents of the padded crop window along the region's long and short axes. */
function cropHalfExtents(r: Region, pad?: number): { halfA: number; halfB: number } {
  const p = pad ?? (r.kind === "2d" ? 1.6 : 1.25);
  return { halfA: r.halfLong * p, halfB: r.halfShort * p * (r.kind === "1d" ? 1.6 : 1.0) };
}

/** Axis-aligned frame rectangle that contains everything `cropRotated` samples for this
 *  region (plus a 2-px bilinear margin), clipped to the frame. The main thread cuts this
 *  sub-image from the full-resolution canvas so a large frame never has to be copied
 *  whole; `cropRotated` is then run on the sub-image with the region shifted by (x0, y0). */
export function cropBounds(r: Region, width: number, height: number): { x0: number; y0: number; width: number; height: number } | null {
  const { halfA, halfB } = cropHalfExtents(r);
  const c = Math.abs(Math.cos(r.angle)), s = Math.abs(Math.sin(r.angle));
  const ex = c * halfA + s * halfB + 2;
  const ey = s * halfA + c * halfB + 2;
  const x0 = Math.max(0, Math.floor(r.center.x - ex));
  const y0 = Math.max(0, Math.floor(r.center.y - ey));
  const x1 = Math.min(width, Math.ceil(r.center.x + ex));
  const y1 = Math.min(height, Math.ceil(r.center.y + ey));
  if (x1 - x0 < 4 || y1 - y0 < 4) return null;
  return { x0, y0, width: x1 - x0, height: y1 - y0 };
}

/** The same region expressed in the coordinates of a sub-image whose origin is (dx, dy). */
export function shiftRegion(r: Region, dx: number, dy: number): Region {
  const mv = (p: Point): Point => ({ x: p.x + dx, y: p.y + dy });
  return { ...r, center: mv(r.center), corners: r.corners.map(mv) as [Point, Point, Point, Point] };
}

export function cropRotated(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  r: Region,
  opts: { pad?: number; scale?: number; maxArea?: number } = {},
): Crop {
  const { halfA, halfB } = cropHalfExtents(r, opts.pad);
  // Bound the work the decoder is handed: an oversized region (a whole label edge, a
  // striped carton) is shrunk rather than decoded at full size.
  const maxArea = opts.maxArea ?? 300_000;
  const area = halfA * halfB * 4;
  let scale = opts.scale ?? 1;
  if (area * scale * scale > maxArea) scale = Math.sqrt(maxArea / area);

  // Nearest axis and the tilt from it.
  const quarter = Math.round(r.angle / (Math.PI / 2));
  const tilt = r.angle - quarter * (Math.PI / 2);
  const swapped = quarter % 2 !== 0; // long axis near vertical
  const maxTilt = Math.atan((0.8 * r.halfShort) / Math.max(1, r.halfLong));
  if (r.kind === "1d" && scale === 1 && Math.abs(tilt) <= maxTilt) {
    const ex = swapped ? halfB : halfA;
    const ey = swapped ? halfA : halfB;
    const x0 = Math.max(0, Math.round(r.center.x - ex));
    const y0 = Math.max(0, Math.round(r.center.y - ey));
    const x1 = Math.min(width, Math.round(r.center.x + ex));
    const y1 = Math.min(height, Math.round(r.center.y + ey));
    const cw = Math.max(1, x1 - x0);
    const ch = Math.max(1, y1 - y0);
    const out = new Uint8ClampedArray(cw * ch * 4);
    for (let y = 0; y < ch; y++) {
      let i = ((y0 + y) * width + x0) * 4;
      let o = y * cw * 4;
      for (let x = 0; x < cw; x++, i += 4, o += 4) {
        const v = (rgba[i]! * 77 + rgba[i + 1]! * 150 + rgba[i + 2]! * 29) >> 8;
        out[o] = out[o + 1] = out[o + 2] = v;
        out[o + 3] = 255;
      }
    }
    return { data: out, width: cw, height: ch, resampled: false, toFrame: (p) => ({ x: x0 + p.x, y: y0 + p.y }) };
  }

  const cw = Math.max(16, Math.round(halfA * 2 * scale));
  const ch = Math.max(16, Math.round(halfB * 2 * scale));
  const out = new Uint8ClampedArray(cw * ch * 4);
  const cos = Math.cos(r.angle) / scale;
  const sin = Math.sin(r.angle) / scale;
  const maxX = width - 2;
  const maxY = height - 2;
  const l = (i: number) => (rgba[i]! * 77 + rgba[i + 1]! * 150 + rgba[i + 2]! * 29) >> 8;
  for (let y = 0; y < ch; y++) {
    const b = y - ch / 2;
    for (let x = 0; x < cw; x++) {
      const a = x - cw / 2;
      const sx = r.center.x + a * cos - b * sin;
      const sy = r.center.y + a * sin + b * cos;
      const o = (y * cw + x) * 4;
      if (sx < 0 || sy < 0 || sx > maxX || sy > maxY) {
        out[o] = out[o + 1] = out[o + 2] = 255;
        out[o + 3] = 255;
        continue;
      }
      const x0 = sx | 0;
      const y0 = sy | 0;
      const fx = sx - x0;
      const fy = sy - y0;
      const i00 = (y0 * width + x0) * 4;
      const i01 = i00 + width * 4;
      const v = (l(i00) * (1 - fx) + l(i00 + 4) * fx) * (1 - fy) + (l(i01) * (1 - fx) + l(i01 + 4) * fx) * fy;
      out[o] = out[o + 1] = out[o + 2] = v;
      out[o + 3] = 255;
    }
  }
  return {
    data: out,
    width: cw,
    height: ch,
    resampled: true,
    toFrame: (p) => {
      const a = p.x - cw / 2;
      const b = p.y - ch / 2;
      return { x: r.center.x + a * cos - b * sin, y: r.center.y + a * sin + b * cos };
    },
  };
}

/** Pixels the localiser's edge pass is allowed to cover; above this the frame is reduced
 *  first. Distant codes survive the reduction (they are dense-edge blobs either way) and
 *  the decode crops are always cut from the full frame. */
export const LOCATE_BUDGET_PX = 2_600_000;

/** Localise on a full-resolution RGBA frame, reducing it first if it is large. Regions
 *  come back in frame coordinates. */
export function locateFactor(width: number, height: number, budgetPx = LOCATE_BUDGET_PX): number {
  return Math.max(1, Math.ceil(Math.sqrt((width * height) / budgetPx)));
}

export function locateFrame(rgba: Uint8ClampedArray | Uint8Array, width: number, height: number, opts: LocateOptions = {}): { regions: Region[]; factor: number } {
  const factor = locateFactor(width, height, opts.budgetPx);
  const regions = locate(toGray(rgba, width, height, factor), opts);
  return { regions: factor === 1 ? regions : regions.map((r) => scaleRegion(r, factor)), factor };
}
